"use client";

import type { IExtraUserData } from "@/app/[project-id]/_utils/types/types.ts";
import { CHART_COLORS } from "@/app/_utils/data/chartColors.ts";
import {
  COGNITO_PRESENTATION_MODE_ATTRIBUTE,
  PRESENTATION_ORGANIZATION_ID,
  findPresentationOrganization,
} from "@/app/_utils/constants/presentationMode.ts";
import { ErrorToastContext } from "@/app/_utils/context/ErrorToastContext.tsx";
import { PresentationModeContext } from "@/app/_utils/context/PresentationModeContext.tsx";
import { UserContext } from "@/app/_utils/context/UserDataContext.tsx";
import type { IOrganizationDocument } from "@/app/_utils/database/db-models/organization.ts";
import { useRequest } from "@/app/_utils/hooks/useRequest.ts";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ScrollShadows } from "@/components/ui/scroll-area";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { progress } from "@/app/_utils/components/NavigationProgress";
import { useProgressRouter as useRouter } from "@/app/_utils/hooks/useProgressRouter";
import {
  IconBattery,
  IconBattery1,
  IconBattery2,
  IconBattery3,
  IconBattery4,
  IconBatteryOff,
  IconBook,
  IconBuilding,
  IconChalkboard,
  IconChevronDown,
  IconLoader2,
  IconLogout,
  IconShare3,
  IconSparkles,
  IconUser,
} from "@tabler/icons-react";
import Link from "next/link";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import type { TUser } from "../functions/getUser.ts";

/**
 * Live battery indicator for the AWS SSO interactive session.
 * Level is derived from the session window, not the access token TTL.
 * Updates every 60 s.
 *
 * @param props - Session expiry and start timestamps (`expiresAt`, `startAt`).
 *
 * @returns Battery icon with tooltip showing remaining time.
 */
function SsoBatteryIndicator({
  expiresAt,
  startAt,
}: {
  expiresAt: string;
  startAt: string;
}): React.ReactElement {
  const totalMs =
    new Date(expiresAt).getTime() - new Date(startAt).getTime();

  const [remaining, setRemaining] = useState(
    () => new Date(expiresAt).getTime() - Date.now(),
  );

  useEffect(() => {
    const id = setInterval(() => {
      setRemaining(new Date(expiresAt).getTime() - Date.now());
    }, 60_000);

    return () => { clearInterval(id); };
  }, [expiresAt]);

  const level = totalMs > 0 ? Math.max(0, remaining / totalMs) : 0;
  const hoursLeft = Math.max(0, Math.floor(remaining / 3_600_000));
  const minutesLeft = Math.max(
    0,
    Math.floor((remaining % 3_600_000) / 60_000),
  );

  let Icon = IconBatteryOff;
  if (level > 0.75) Icon = IconBattery4;
  else if (level > 0.5) Icon = IconBattery3;
  else if (level > 0.25) Icon = IconBattery2;
  else if (level > 0.1) Icon = IconBattery1;
  else if (level > 0) Icon = IconBattery;

  let color: string = CHART_COLORS.gray;
  if (level > 0.5) color = CHART_COLORS.success;
  else if (level > 0.25) color = CHART_COLORS.warning;
  else if (level > 0) color = CHART_COLORS.error;

  const label =
    remaining > 0
      ? `AWS SSO: ${hoursLeft}h ${minutesLeft}m remaining`
      : "AWS SSO: session expired";

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            aria-label={label}
            className="flex items-center [&>svg]:!w-10 [&>svg]:!h-10"
          >
            <Icon color={color} />
          </span>
        </TooltipTrigger>
        <TooltipContent>{label}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * Builds user widget dropdown.
 *
 * @param params - Parameters from the parent object.
 *
 * @returns Formatted dropdown.
 */
export default function UserWidgetDropDown({
  initialData,
  ssoExpiresAt,
  ssoSessionStartAt,
}: {
  initialData: {
    "user": TUser | null;
    "organizations": Partial<IOrganizationDocument>[];
    "sharedOrganizations": Partial<IOrganizationDocument>[];
    "activeOrganization": string;
    "picture": string;
    "extra": IExtraUserData;
  };
  "ssoExpiresAt"?: string | null;
  "ssoSessionStartAt"?: string | null;
}): React.ReactElement {
  const { setToastError } = useContext(ErrorToastContext);
  const { user, setUser } = useContext(UserContext);
  const { presentationModeEnabled, canUsePresentationMode } = useContext(
    PresentationModeContext,
  );
  const router = useRouter();
  const { makeRequest } = useRequest(setToastError);
  const [isOpen, setIsOpen] = useState(false);
  const [presentationToggleBusy, setPresentationToggleBusy] = useState(false);
  const [orgImages, setOrgImages] = useState<Record<string, string>>({});
  const orgScrollRef = useRef<HTMLDivElement>(null);
  const ssoActive =
    ssoExpiresAt !== null && ssoExpiresAt !== undefined &&
    ssoSessionStartAt !== null && ssoSessionStartAt !== undefined;
  /**
   * Synchronous guard before `presentationToggleBusy` is committed (same-tick double `onChange`).
   * Busy state handles UI and normal re-entrancy after paint.
   */
  const presentationToggleInFlightRef = useRef(false);

  // Track which org IDs we've already fetched to prevent duplicate requests
  const fetchedOrgIds = useRef<Set<string>>(new Set());

  // The initial render of the this component may happen before the server
  // component has finished fetching new credentials. Without this useEffect,
  // user drop down component would be missing after the initial load.
  useEffect(() => {
    if (initialData.user !== null) {
      setUser((prevUser) => {
        if (
          prevUser?.sub === initialData.user?.sub &&
          prevUser?.activeOrganization === initialData.activeOrganization &&
          prevUser.organizations?.length === initialData.organizations.length &&
          prevUser.sharedOrganizations?.length ===
            initialData.sharedOrganizations.length &&
          prevUser[COGNITO_PRESENTATION_MODE_ATTRIBUTE] ===
            initialData.user?.[COGNITO_PRESENTATION_MODE_ATTRIBUTE]
        ) {
          return prevUser;
        }

        return {
          ...initialData.user,
          "organizations": initialData.organizations,
          "sharedOrganizations": initialData.sharedOrganizations,
          "activeOrganization": initialData.activeOrganization,
          "picture": initialData.picture,
          "extra": initialData.extra,
        };
      });
    }
  }, [setUser, initialData]);

  // Fetch organization images
  useEffect(() => {
    const fetchOrgImages = async (): Promise<void> => {
      if (user === null) {
        return;
      }

      const allOrgs = [
        ...(user.organizations ?? []),
        ...(user.sharedOrganizations ?? []),
      ];

      // Filter out orgs we've already fetched
      const orgsNeedingImages = allOrgs.filter(
        (org) =>
          org._id !== undefined &&
          org._id !== "" &&
          !fetchedOrgIds.current.has(org._id),
      );

      // If we already have all images, don't fetch again
      if (orgsNeedingImages.length === 0) {
        return;
      }

      // Mark these orgs as being fetched
      const orgIdsToFetch = orgsNeedingImages
        .map((org) => org._id)
        .filter((id): id is string => typeof id === "string" && id !== "");

      orgIdsToFetch.forEach((id) => {
        fetchedOrgIds.current.add(id);
      });

      try {
        // Fetch all images in one request
        const response = await fetch("/api/v1/organization/get-org-pics", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ orgIds: orgIdsToFetch }),
          cache: "no-store",
        });

        if (!response.ok) {
          orgIdsToFetch.forEach((id) => fetchedOrgIds.current.delete(id));
          console.warn(
            `Failed to fetch organization images: ${response.status}`,
          );

          return;
        }

        const result = (await response.json()) as {
          data: Record<string, string[]>;
        };

        setOrgImages((prev) => {
          const newImages = { ...prev };

          Object.entries(result.data).forEach(([orgId, urls]) => {
            if (urls.length > 0 && urls[0]) {
              newImages[orgId] = urls[0];
            }
          });

          return newImages;
        });
      } catch (error) {
        orgIdsToFetch.forEach((id) => fetchedOrgIds.current.delete(id));
        console.error("Failed to fetch organization images:", error);
      }
    };

    fetchOrgImages().catch((error) => {
      console.error("Failed to fetch organization images:", error);
    });
  }, [user]);

  const switchActiveOrganization = useCallback(
    async (org: Partial<IOrganizationDocument>): Promise<void> => {
      if (user === null) {
        return;
      }

      // Remove the user from ALL orgs' activeUsers first, then add to the
      // target org.  Using updateMany instead of updateOne guards against stale
      // client state where user.activeOrganization doesn't match the DB, which
      // would leave the user in activeUsers for multiple orgs simultaneously.
      await makeRequest<string>({
        requestSlug: `switch-organization-${org._id ?? "unknown"}`,
        url: "/api/v1/organization/set-active",
        payload: { orgId: org._id },
        returnPromise: true,
      });

      setUser({
        ...user,
        activeOrganization: org._id ?? "Error",
      });
    },
    [makeRequest, setUser, user],
  );

  const commitPresentationToggle = useCallback(
    async (wantEnabled: boolean): Promise<void> => {
      if (!canUsePresentationMode) {
        return;
      }

      if (presentationToggleInFlightRef.current) {
        return;
      }

      if (wantEnabled === presentationModeEnabled) {
        return;
      }

      presentationToggleInFlightRef.current = true;
      setPresentationToggleBusy(true);

      const persistPresentationAttribute = async (
        enabled: boolean,
      ): Promise<void> => {
        await makeRequest<TUser | null>({
          requestSlug: "persist-presentation-mode",
          url: "/api/v1/profile/update-profile",
          payload: [
            {
              Name: COGNITO_PRESENTATION_MODE_ATTRIBUTE,
              Value: enabled ? "true" : "false",
            },
          ],
          returnPromise: true,
        });
      };

      // Ordering matters because the DB write guard reads the Cognito JWT:
      //   Enable:  org switch (write) FIRST → then set attribute (activates guard)
      //   Disable: clear attribute (deactivates guard) FIRST → then navigate
      // Reversing the enable order would block the org switch bulkWrite with a
      // PresentationModeWriteError since the cookie is refreshed inline.
      try {
        if (!wantEnabled) {
          await persistPresentationAttribute(false);
          setIsOpen(false);
          progress.start();
          router.push("/");
          router.refresh();

          return;
        }

        if (user === null) {
          return;
        }

        const demoOrg = findPresentationOrganization(user);
        if (typeof demoOrg?._id !== "string" || demoOrg._id === "") {
          return;
        }

        // Inline the org switch instead of calling switchActiveOrganization
        // to avoid the setUser() call that would trigger a re-render showing
        // all PhaseTree projects before the redirect fires.
        await makeRequest<string>({
          requestSlug: `switch-organization-${demoOrg._id}`,
          url: "/api/v1/organization/set-active",
          payload: { orgId: demoOrg._id },
          returnPromise: true,
        });

        await persistPresentationAttribute(true);
        setIsOpen(false);
        progress.start();
        router.push("/");
        router.refresh();
      } catch (error) {
        console.error("Failed to update presentation mode:", error);
        throw error;
      } finally {
        presentationToggleInFlightRef.current = false;
        setPresentationToggleBusy(false);
      }
    },
    [canUsePresentationMode, makeRequest, presentationModeEnabled, router, user],
  );

  if (user === null) {
    return <></>;
  }

  // Find active organization in both regular and shared organizations
  const activeOrg =
    user.organizations?.find((org) => org._id === user.activeOrganization) ??
    user.sharedOrganizations?.find(
      (org) => org._id === user.activeOrganization,
    ) ??
    user.organizations?.[0];

  const getUserInitials = (name: string): string => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const renderOrgItem = (org: Partial<IOrganizationDocument>): React.ReactElement => (
    <DropdownMenuItem
      key={org._id ?? org.name}
      onSelect={(event) => {
        if (
          presentationModeEnabled &&
          !isPresentationOrgId(org._id)
        ) {
          event.preventDefault();
        }
      }}
      onClick={() => {
        handleOrganizationSwitch(org).catch((error) => {
          console.error("Organization switch failed:", error);
        });
      }}
      className={cn(
        "flex items-center justify-between cursor-pointer mx-1 px-3 py-3 rounded-xl transition-all duration-200",
        org._id === user.activeOrganization
          ? "bg-gradient-to-r from-primary/10 to-primary/5 shadow-sm ring-1 ring-primary/20"
          : "hover:bg-gray-50 hover:shadow-sm",
        presentationModeEnabled &&
          !isPresentationOrgId(org._id) &&
          "opacity-60 cursor-not-allowed hover:bg-transparent hover:shadow-none",
      )}
    >
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <div
          className={cn(
            "flex-shrink-0 rounded-full",
            org._id === user.activeOrganization &&
              "ring-2 ring-primary/30",
          )}
        >
          <div
            className={cn(
              "overflow-hidden rounded-full",
              presentationModeEnabled &&
                !isPresentationOrgId(org._id) &&
                "blur-[6px] select-none",
            )}
          >
            <Avatar className="w-9 h-9 bg-gradient-to-br from-primary/20 to-primary/10 border-2 border-white shadow-sm">
              <AvatarImage
                src={
                  orgImages[org._id ?? ""] &&
                  orgImages[org._id ?? ""]?.trim() !== ""
                    ? orgImages[org._id ?? ""]
                    : undefined
                }
                alt={`${org.name ?? ""} avatar`}
                className="object-cover"
              />
              <AvatarFallback className="bg-gradient-to-br from-primary/30 to-primary/20 text-primary text-sm font-bold">
                {(org.name?.[0] ?? "O").toUpperCase()}
              </AvatarFallback>
            </Avatar>
          </div>
        </div>
        <span
          className={cn(
            "text-sm truncate",
            org._id === user.activeOrganization
              ? "font-semibold text-gray-900"
              : "font-medium text-gray-700",
            presentationModeEnabled &&
              !isPresentationOrgId(org._id) &&
              "blur-[6px] select-none",
          )}
        >
          {org.name}
        </span>
      </div>
      {org._id === user.activeOrganization && (
        <Badge className="ml-2 text-xs font-semibold bg-primary text-white border-0 shadow-sm">
          Active
        </Badge>
      )}
    </DropdownMenuItem>
  );

  const handleOrganizationSwitch = async (
    org: Partial<IOrganizationDocument>,
  ): Promise<void> => {
    if (
      presentationModeEnabled &&
      org._id?.toLowerCase() !== PRESENTATION_ORGANIZATION_ID.toLowerCase()
    ) {
      return;
    }

    try {
      await switchActiveOrganization(org);
      router.push("/");
      setIsOpen(false);
    } catch (error) {
      console.error("Failed to switch organization:", error);
    }
  };

  const isPresentationOrgId = (orgId: string | undefined): boolean =>
    typeof orgId === "string" &&
    orgId.toLowerCase() === PRESENTATION_ORGANIZATION_ID.toLowerCase();

  const handleLogout = (): void => {
    fetch(`${process.env.URL ?? ""}/api/auth/log-out`, {
      "cache": "no-store",
      "method": "POST",
    })
      .then(() => {
        router.push("/log-in");
        router.refresh();
      })
      .catch((error) => {
        console.error(error);
      });
    setIsOpen(false);
  };

  return (
    <div className="absolute right-0 mr-4">
      <DropdownMenu modal={false} open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            className={cn(
              "flex h-auto items-center gap-3 p-2",
              "hover:bg-accent",
            )}
          >
            {presentationModeEnabled ? (
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-orange-100 ring-1 ring-orange-300/50">
                <span
                  className="text-sm font-bold leading-none text-orange-600"
                  aria-hidden
                >
                  P
                </span>
              </span>
            ) : null}
            <Avatar className="h-8 w-8">
              <AvatarImage src={user.picture ?? ""} alt={user.name ?? ""} />
              <AvatarFallback className="text-xs">
                {getUserInitials(user.name ?? "U")}
              </AvatarFallback>
            </Avatar>

            <div className="hidden lg:flex flex-col items-start text-left">
              <span className="text-sm font-medium text-foreground">
                {user.name ?? ""}
              </span>
              <span className="text-xs text-muted-foreground">
                @ {activeOrg?.name}
              </span>
            </div>

            {ssoActive ? (
              <SsoBatteryIndicator
                expiresAt={ssoExpiresAt}
                startAt={ssoSessionStartAt}
              />
            ) : (
              <IconChevronDown className="h-4 w-4 text-muted-foreground" />
            )}
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent
          align="end"
          className="w-80 shadow-xl border-0 rounded-2xl p-0 overflow-hidden bg-white"
        >
          {/* Mobile user info */}
          <div className="lg:hidden bg-gradient-to-br from-primary/5 to-primary/10 px-4 py-4 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <Avatar className="h-10 w-10 ring-2 ring-white shadow-sm">
                <AvatarImage src={user.picture ?? ""} alt={user.name ?? ""} />
                <AvatarFallback className="bg-primary/20 text-primary font-semibold">
                  {getUserInitials(user.name ?? "U")}
                </AvatarFallback>
              </Avatar>
              <div className="flex flex-col">
                <p className="text-sm font-semibold text-gray-900">
                  {user.name ?? ""}
                </p>
                <p className="text-xs text-gray-600">{user.email ?? ""}</p>
              </div>
            </div>
          </div>

          {/* Organizations scrollable section */}
          <div className="relative">
            <div
              ref={orgScrollRef}
              className="max-h-96 overflow-y-auto px-2 py-3"
            >
              {/* My Organizations section */}
              <div className="mb-3">
                <div className="flex items-center gap-2 px-3 py-2 mb-2">
                  <div className="flex items-center justify-center w-6 h-6 rounded-md bg-primary/10">
                    <IconBuilding className="h-3.5 w-3.5 text-primary" />
                  </div>
                  <span className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                    My Organizations
                  </span>
                </div>

                <div className="space-y-1">
                  {typeof user.organizations === "undefined" ? (
                    <></>
                  ) : (
                    user.organizations.map(renderOrgItem)
                  )}
                </div>
              </div>

              {/* Shared with me organizations */}
              {user.sharedOrganizations &&
                user.sharedOrganizations.length > 0 && (
                  <>
                    <div className="h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent my-3" />
                    <div className="mb-3">
                      <div className="flex items-center gap-2 px-3 py-2 mb-2">
                        <div className="flex items-center justify-center w-6 h-6 rounded-md bg-primary/10">
                          <IconShare3 className="h-3.5 w-3.5 text-primary" />
                        </div>
                        <span className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                          Shared with me
                        </span>
                      </div>

                      <div className="space-y-1">
                        {user.sharedOrganizations.map(renderOrgItem)}
                      </div>
                    </div>
                  </>
                )}
            </div>
            <ScrollShadows scrollRef={orgScrollRef} />
          </div>

          {/* Fixed footer section */}
          <div className="border-t border-gray-100 bg-gray-50/50 px-2 py-2">
            {canUsePresentationMode ? (
              <DropdownMenuItem
                className={cn(
                  "mx-1 mb-1 w-full min-w-0 cursor-pointer rounded-xl px-3 py-2.5 transition-all hover:bg-white hover:shadow-sm data-[highlighted]:bg-white",
                  presentationToggleBusy && "pointer-events-none opacity-70",
                )}
                onSelect={(event) => {
                  event.preventDefault();
                }}
              >
                <label className="flex w-full min-w-0 cursor-pointer items-center gap-3">
                  <input
                    type="checkbox"
                    name="presentation-mode"
                    checked={presentationModeEnabled}
                    disabled={presentationToggleBusy}
                    aria-busy={presentationToggleBusy}
                    aria-label="Presentation mode"
                    onChange={(event) => {
                      commitPresentationToggle(event.target.checked).catch(
                        () => undefined,
                      );
                    }}
                    className="sr-only"
                  />
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-gray-100 bg-white shadow-sm">
                    <IconChalkboard className="h-4 w-4 text-gray-600" />
                  </div>
                  <span className="min-w-0 flex-1 text-left text-sm font-medium leading-none text-gray-700">
                    Presentation mode
                  </span>
                  <span
                    aria-hidden
                    className={cn(
                      "relative inline-flex h-6 w-11 shrink-0 rounded-full p-0.5 transition-colors duration-200 ease-out",
                      presentationModeEnabled ? "bg-primary" : "bg-gray-200",
                    )}
                  >
                    <span
                      className={cn(
                        "pointer-events-none absolute top-1/2 block size-5 -translate-y-1/2 rounded-full bg-white shadow-sm transition-all duration-200 ease-out",
                        presentationModeEnabled
                          ? "left-auto right-0.5"
                          : "left-0.5 right-auto",
                        presentationToggleBusy && "opacity-0",
                      )}
                    />
                    {presentationToggleBusy ? (
                      <span
                        className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-full bg-black/15"
                        aria-hidden
                      >
                        <IconLoader2
                          className={cn(
                            "size-3.5 shrink-0 animate-spin",
                            presentationModeEnabled
                              ? "text-white"
                              : "text-gray-600",
                          )}
                        />
                      </span>
                    ) : null}
                  </span>
                </label>
              </DropdownMenuItem>
            ) : null}

            {/* Navigation items */}
            <div className="space-y-0.5 mb-1">
              <DropdownMenuItem
                asChild
                className="cursor-pointer no-underline mx-1 px-3 py-2.5 rounded-xl hover:bg-white hover:shadow-sm transition-all"
              >
                <Link
                  href="/profile"
                  className="flex w-full min-w-0 items-center gap-3"
                >
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-gray-100 bg-white shadow-sm">
                    <IconUser className="h-4 w-4 text-gray-600" />
                  </div>
                  <span className="min-w-0 flex-1 text-left text-sm font-medium leading-none text-gray-700">
                    Profile
                  </span>
                </Link>
              </DropdownMenuItem>

              {!presentationModeEnabled && (
                <DropdownMenuItem
                  asChild
                  className="cursor-pointer no-underline mx-1 px-3 py-2.5 rounded-xl hover:bg-white hover:shadow-sm transition-all"
                >
                  <Link
                    href="/skills"
                    className="flex w-full min-w-0 items-center gap-3"
                  >
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-gray-100 bg-white shadow-sm">
                      <IconSparkles className="h-4 w-4 text-gray-600" />
                    </div>
                    <span className="min-w-0 flex-1 text-left text-sm font-medium leading-none text-gray-700">
                      Skills
                    </span>
                  </Link>
                </DropdownMenuItem>
              )}

              {!presentationModeEnabled && (
                <DropdownMenuItem
                  asChild
                  className="cursor-pointer no-underline mx-1 px-3 py-2.5 rounded-xl hover:bg-white hover:shadow-sm transition-all"
                >
                  <Link
                    href="/organization"
                    className="flex w-full min-w-0 items-center gap-3"
                  >
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-gray-100 bg-white shadow-sm">
                      <IconBuilding className="h-4 w-4 text-gray-600" />
                    </div>
                    <span className="min-w-0 flex-1 text-left text-sm font-medium leading-none text-gray-700">
                      Organizations
                    </span>
                  </Link>
                </DropdownMenuItem>
              )}

              <DropdownMenuItem
                asChild
                className="cursor-pointer no-underline mx-1 px-3 py-2.5 rounded-xl hover:bg-white hover:shadow-sm transition-all"
              >
                <Link
                  href="/docs"
                  className="flex w-full min-w-0 items-center gap-3"
                >
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-gray-100 bg-white shadow-sm">
                    <IconBook className="h-4 w-4 text-gray-600" />
                  </div>
                  <span className="min-w-0 flex-1 text-left text-sm font-medium leading-none text-gray-700">
                    Documentation
                  </span>
                </Link>
              </DropdownMenuItem>
            </div>

            <div className="h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent my-2" />

            <DropdownMenuItem
              onClick={handleLogout}
              className="flex w-full min-w-0 cursor-pointer items-center gap-3 rounded-xl px-3 py-2.5 text-red-600 transition-all hover:bg-red-50 hover:shadow-sm focus:bg-red-50 focus:text-red-600 mx-1"
            >
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-red-100 bg-red-50">
                <IconLogout className="h-4 w-4 text-red-600" />
              </div>
              <span className="min-w-0 flex-1 text-left text-sm font-semibold leading-none">
                Log out
              </span>
            </DropdownMenuItem>
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
